// src/excel/builders/tsl/buildWorkbook.js
// XLSX builder for the TSL (Time Spent Listening) service

export function buildTslWorkbook(XLSX, params) {
  const {
    t,
    shortcode,
    stationName,
    serviceName,
    period,
    date1,
    labels = [],
    data = [],
    avg,
    start,
    end,
    totalSessions,
    fmtNumber,
    locale
  } = params || {};

  const wb = XLSX.utils.book_new();
  const ws = {};
  const merges = [];

  const brandText = (t && t('listeners.brand_line_excel')) || 'Radiomonitor.Net';
  const serviceTitle = serviceName || (t && t('tsl.title')) || 'TSL (Time Spent Listening)';
  const stationTitle = stationName || shortcode || '';
  const periodLabel = period === 'day'
    ? (t && t('tsl.date_label_day')) || 'Date:'
    : (t && t('tsl.date_label_last')) || 'Last date:';
  const dateValue = date1 || '';

  const avgText = totalSessions > 0
    ? `${(t && t('tsl.avg_prefix')) || 'Average'} ${fmtNumber ? fmtNumber(avg ?? 0, locale) : (avg ?? 0)} ${(t && t('tsl.minutes')) || 'minutes'}`
    : (t && t('tsl.no_data')) || 'No data';

  const periodRangeText = (totalSessions > 0 && start && end)
    ? (t ? t('tsl.period', { start, end, count: totalSessions, keySeparator: false }) : `Period: ${start} – ${end} (${totalSessions})`)
    : '';

  let currentRow = 0;

  // Brand row
  XLSX.utils.sheet_add_aoa(ws, [[brandText]], { origin: { r: currentRow, c: 0 } });
  merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 3 } });
  const brandAddr = XLSX.utils.encode_cell({ r: currentRow, c: 0 });
  ws[brandAddr] = ws[brandAddr] || {};
  ws[brandAddr].s = {
    font: { bold: true, sz: 20, color: { rgb: '1F2A5A' } },
    fill: { fgColor: { rgb: 'EEF2FF' } },
    alignment: { horizontal: 'left', vertical: 'center' }
  };
  currentRow += 2;

  const infoRows = [
    [serviceTitle],
    ...(stationTitle ? [[stationTitle]] : []),
    [`${periodLabel} ${dateValue}`],
    [avgText],
    ...(periodRangeText ? [[periodRangeText]] : []),
    ['']
  ];

  XLSX.utils.sheet_add_aoa(ws, infoRows, { origin: { r: currentRow, c: 0 } });
  for (let i = 0; i < infoRows.length; i += 1) {
    merges.push({ s: { r: currentRow + i, c: 0 }, e: { r: currentRow + i, c: 3 } });
  }

  const encode = XLSX.utils.encode_cell;
  const serviceAddr = encode({ r: currentRow, c: 0 });
  if (ws[serviceAddr]) {
    ws[serviceAddr].s = {
      font: { bold: true, sz: 18, color: { rgb: '1F1F1F' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
  }

  if (stationTitle) {
    const stationAddr = encode({ r: currentRow + 1, c: 0 });
    if (ws[stationAddr]) {
      ws[stationAddr].s = {
        font: { bold: true, sz: 16, color: { rgb: '1F1F1F' } },
        alignment: { horizontal: 'left', vertical: 'center' }
      };
    }
  }

  currentRow += infoRows.length;

  const headerRow = [
    (t && t('tsl.export_label_header')) || 'Label',
    (t && t('tsl.minutes')) || 'Minutes'
  ];

  const tableRows = labels.map((label, idx) => {
    const rawValue = data[idx];
    const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue || 0);
    return [
      label ?? '',
      Number.isFinite(numericValue) ? numericValue : 0
    ];
  });

  const table = [
    headerRow,
    ...tableRows
  ];

  XLSX.utils.sheet_add_aoa(ws, table, { origin: { r: currentRow, c: 0 } });

  const headerRowIndex = currentRow;
  const totalRowIndex = currentRow + table.length - 1;

  const headerStyle = {
    font: { bold: true, color: { rgb: '333333' } },
    fill: { fgColor: { rgb: 'F0F0F0' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: 'D0D0D0' } },
      bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
      left: { style: 'thin', color: { rgb: 'D0D0D0' } },
      right: { style: 'thin', color: { rgb: 'D0D0D0' } }
    }
  };

  const borderStyle = {
    border: {
      top: { style: 'thin', color: { rgb: 'E0E0E0' } },
      bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
      left: { style: 'thin', color: { rgb: 'E0E0E0' } },
      right: { style: 'thin', color: { rgb: 'E0E0E0' } }
    }
  };

  for (let c = 0; c < headerRow.length; c += 1) {
    const addr = encode({ r: headerRowIndex, c });
    if (ws[addr]) ws[addr].s = headerStyle;
  }

  for (let r = headerRowIndex + 1; r <= totalRowIndex; r += 1) {
    for (let c = 0; c < headerRow.length; c += 1) {
      const addr = encode({ r, c });
      if (!ws[addr]) continue;
      ws[addr].s = {
        ...(ws[addr].s || {}),
        ...borderStyle,
        alignment: {
          horizontal: c === 0 ? 'left' : 'right',
          vertical: 'center'
        },
        font: { sz: 11, color: { rgb: '1F1F1F' } }
      };
      if (c === 1 && typeof ws[addr].v === 'number') {
        ws[addr].t = 'n';
        ws[addr].z = '#,##0.00';
      }
    }
  }

  ws['!cols'] = [
    { wch: 24 },
    { wch: 16 }
  ];

  ws['!merges'] = merges;

  if (table.length > 1) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: totalRowIndex, c: headerRow.length - 1 }
      })
    };
  }

  try {
    ws['!freeze'] = { xSplit: 1, ySplit: headerRowIndex + 1 };
  } catch {
    // ignore freeze errors
  }

  XLSX.utils.book_append_sheet(wb, ws, 'TSL');
  return wb;
}

export default buildTslWorkbook;
